/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/infrastructure/lib/utils';
import { getLocalizedText } from '@/shared/utils/i18nHelper';

interface MetricChartProps {
  // Aceita { avg, count, ... } direto ou array com 1 elemento.
  data: any;
  config: {
    valueField?: string;
    decimals?: number;
    valuePrefix?: string;
    valueSuffix?: string;
    subtitleValueField?: string;
    // String legada ou objeto multilíngue {en, pt}. Resolvido via i18n.language.
    subtitleLabel?: string | Record<string, string>;
  };
  // Filter values from the dashboard (persisted + inline). subtitleLabel may
  // reference these via {{key}} placeholders so the subtitle reflects the
  // current filter (e.g. "novos ({{days}}d)" → "novos (30d)").
  filterValues?: Record<string, unknown>;
}

function interpolate(template: string, values: Record<string, unknown>): string {
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key: string) => {
    const v = values[key];
    if (v === undefined || v === null) return '';
    return String(v);
  });
}

function pickRow(data: any): any {
  if (!data) return {};
  if (Array.isArray(data)) return data[0] ?? {};
  return data;
}

function formatNumber(value: unknown, decimals = 1): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (Number.isInteger(n) && decimals === 0) return n.toLocaleString();
  return n.toFixed(decimals);
}

// Tamanho-alvo do MetricChart é o tile mínimo (w=2 h=2 ≈ 200×130 px).
// Em larguras maiores, escalamos proporcionalmente. A escala é função do
// menor lado disponível para não estourar nem horizontal nem verticalmente.
const BASE_VALUE_PX = 60;
const BASE_AFFIX_PX = 26;
const BASE_SUB_PX = 16;
const BASE_GAP_PX = 10;
// Referência: w=2 h=2 ≈ 200×130 (descontando paddings/header). Usamos o
// menor lado como dimensão de referência; em w=2 a largura é mais
// constrangedora (~170px área útil).
const REF_DIM = 130;

export const MetricChart = ({ data, config, filterValues }: MetricChartProps) => {
  const { i18n } = useTranslation();
  const row = pickRow(data);
  const valueField = config?.valueField ?? (row.avg !== undefined ? 'avg' : 'value');
  const decimals = config?.decimals ?? 1;

  const rawValue = row[valueField];
  const hasValue = rawValue !== null && rawValue !== undefined;
  const formattedValue = hasValue ? formatNumber(rawValue, decimals) : '—';

  const subField = config?.subtitleValueField;
  const subRaw = subField ? row[subField] : undefined;
  const subValue = subRaw !== undefined && subRaw !== null ? Number(subRaw).toLocaleString() : null;
  const localizedSubLabel = config?.subtitleLabel
    ? getLocalizedText(config.subtitleLabel, i18n.language)
    : undefined;
  const subLabel = localizedSubLabel && filterValues
    ? interpolate(localizedSubLabel, filterValues)
    : localizedSubLabel;

  // Escala conservadora: 1.0 no tamanho-alvo, até 2.2 em containers grandes.
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      // Limita pela menor das duas dimensões úteis (largura conta como
      // ~ height equivalente dividindo por 2.4 — proporção típica de tile).
      const ref = Math.min(rect.height, rect.width / 2.4);
      const next = Math.max(0.85, Math.min(2.2, ref / REF_DIM));
      setScale(next);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="h-full w-full flex flex-col items-center justify-center text-center px-2 py-2 overflow-hidden"
    >
      <div className="flex items-baseline gap-1">
        {config?.valuePrefix && (
          <span
            className="text-muted-foreground"
            style={{ fontSize: Math.round(BASE_AFFIX_PX * scale), lineHeight: 1 }}
          >
            {config.valuePrefix}
          </span>
        )}
        <span
          className={cn('font-semibold tabular-nums')}
          style={{ fontSize: Math.round(BASE_VALUE_PX * scale), lineHeight: 1 }}
        >
          {formattedValue}
        </span>
        {config?.valueSuffix && (
          <span
            className="text-muted-foreground"
            style={{ fontSize: Math.round(BASE_AFFIX_PX * scale), lineHeight: 1 }}
          >
            {config.valueSuffix}
          </span>
        )}
      </div>
      {(subValue !== null || subLabel) && (
        <div
          className="text-muted-foreground"
          style={{
            fontSize: Math.round(BASE_SUB_PX * scale),
            marginTop: Math.round(BASE_GAP_PX * scale),
            lineHeight: 1.2,
          }}
        >
          {subValue !== null && <span className="font-medium text-foreground">{subValue}</span>}
          {subValue !== null && subLabel ? ' ' : ''}
          {subLabel}
        </div>
      )}
    </div>
  );
};
